/**
 * Multi-select variant selector.
 *
 * Family groups (Dad, Mom, Boy, Girl, ...) render as independent groups.
 * Customers can select multiple sizes per group, set a quantity for each
 * resolved variant, and add everything to the bag in one request.
 *
 * CSS classes use the variant-selector BEM namespace (see variant-selector.css).
 */
(function () {
  'use strict';

  var EMBROIDERY_VARIANT_ID = 43495605647;
  var DEFAULT_QUANTITY_LIMIT = 8;
  var RESTRICTED_QUANTITY_LIMIT = 1;

  // Avoid waking DOM-recording integrations for values that did not change.
  function setTextIfChanged(el, value) {
    var text = String(value);
    if (el.textContent !== text) el.textContent = text;
  }

  function setAttributeIfChanged(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function setHiddenIfChanged(el, hidden) {
    if (el.hidden !== hidden) el.hidden = hidden;
  }

  var GROUP_DEFS = [
    { key: 'DAD', label: 'Dad' },
    { key: 'MOM', label: 'Mom' },
    { key: 'ADULT', label: 'Adult' },
    { key: 'BOY', label: 'Boy' },
    { key: 'GIRL', label: 'Girl' },
    { key: 'BABY', label: 'Baby' },
    { key: 'CHILD', label: 'Child' },
    { key: 'KIDS', label: 'Kids' },
    { key: 'PET', label: 'Pet' },
  ];

  var SIZE_ORDER = [
    'XXXS', 'XXS', 'XS', 'S', 'SM', 'SMALL', 'M', 'MED', 'MEDIUM',
    'L', 'LG', 'LARGE', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL',
  ];

  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function defaultTo(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = defaultTo(cents, 2);
        break;
      case 'amount_no_decimals':
        value = defaultTo(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = defaultTo(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = defaultTo(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = defaultTo(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = defaultTo(cents, 0, ' ', '');
        break;
      default:
        value = defaultTo(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function normalize(value) {
    return value == null ? '' : String(value).trim().toLowerCase();
  }

  function detectGroupKey(value) {
    var upper = String(value).toUpperCase();
    for (var i = 0; i < GROUP_DEFS.length; i++) {
      if (upper.indexOf(GROUP_DEFS[i].key) !== -1) return GROUP_DEFS[i];
    }
    return null;
  }

  function cleanSizeLabel(value, groupKey) {
    var label = String(value).toUpperCase();
    if (groupKey) label = label.split(groupKey).join('');
    label = label.split('FAMILY').join('');
    label = label.replace(/^[\s-]+|[\s-]+$/g, '');
    label = label.replace(/\s*-\s*/g, ' - ');
    return label.trim() || String(value);
  }

  function sizeRank(label) {
    var token = String(label).toUpperCase().split(' - ')[0].replace(/\s/g, '');
    var index = SIZE_ORDER.indexOf(token);
    return index === -1 ? 999 : index;
  }

  function isAccessoryLabel(label) {
    return /\b(?:bows?|head[\s-]*bands?|bandanas?|scrunch(?:ie|y|ies)|hair[\s-]*(?:clips?|ties?))\b/i.test(label);
  }

  function previewSizeLabel(value, groupKey) {
    return cleanSizeLabel(value, groupKey)
      .replace(/(\d)\s*-\s*(?=\d)/g, '$1-')
      .replace(/\b(?:NEWBORN|MONTHS?|BOW|HEADBAND)\b/g, function (word) {
        return word.charAt(0) + word.slice(1).toLowerCase();
      });
  }

  function setClassIfChanged(el, name, enabled) {
    if (el.classList.contains(name) !== !!enabled) el.classList.toggle(name, !!enabled);
  }

  function setSizeSummary(el, labels, separator) {
    var text = labels.join(separator);
    setAttributeIfChanged(el, 'title', text);
    if (el.textContent === text) return;
    var reusable = Array.prototype.slice.call(el.childNodes);
    function take(match, create) {
      var index = reusable.findIndex(match);
      return index < 0 ? create() : reusable.splice(index, 1)[0];
    }
    var desired = [];
    labels.forEach(function (label, index) {
      if (index) desired.push(take(function (node) {
        return node.nodeType === 3 && node.nodeValue === separator;
      }, function () { return document.createTextNode(separator); }));
      desired.push(take(function (node) {
        return node.nodeType === 1 && node.tagName === 'SPAN' &&
          node.className === 'variant-selector-group__size' && node.textContent === label;
      }, function () {
        var size = document.createElement('span');
        size.className = 'variant-selector-group__size';
        size.textContent = label;
        return size;
      }));
    });
    var cursor = el.firstChild;
    desired.forEach(function (node) {
      if (node === cursor) cursor = cursor.nextSibling;
      else el.insertBefore(node, cursor);
    });
    while (cursor) {
      var next = cursor.nextSibling;
      cursor.remove();
      cursor = next;
    }
  }

  function isTopSize(value) {
    var upper = String(value).toUpperCase();
    if (upper.indexOf('BOTTOM') !== -1) return false;
    return (
      upper.indexOf('MOM-TOP') !== -1 ||
      upper.indexOf('ADULT-TOP') !== -1 ||
      (upper.indexOf('-TOP') !== -1 && upper.indexOf('BOTTOM') === -1)
    );
  }

  function isBottomSize(value) {
    var upper = String(value).toUpperCase();
    if (upper.indexOf('TOP') !== -1 && upper.indexOf('BOTTOM') === -1) return false;
    return (
      upper.indexOf('MOM-BOTTOM') !== -1 ||
      upper.indexOf('ADULT-BOTTOM') !== -1 ||
      upper.indexOf('-BOTTOM') !== -1
    );
  }

  function groupElementId(uid, groupId) {
    return uid + '-group-' + groupId;
  }

  function newSetId() {
    return 'set-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  function VariantSelector(root) {
    var dataNode = root.querySelector('[data-variant-data]');
    if (!dataNode) {
      setClassIfChanged(root, 'variant-selector--initial-loading', false);
      return;
    }

    try {
      this.data = JSON.parse(dataNode.textContent);
    } catch (error) {
      setClassIfChanged(root, 'variant-selector--initial-loading', false);
      return;
    }

    this.root = root;
    if ((this.data.options || []).some(function (option) { return /^grade$/i.test(option.name); })) {
      this.data.singleVariantLimit = true;
    }
    this.uidPrefix = root.getAttribute('data-vs-uid') || 'variant-selector';
    this.quantityLimit = (this.data.isMysteryBox || this.data.hasEmbroidery || this.data.singleVariantLimit)
      ? RESTRICTED_QUANTITY_LIMIT
      : DEFAULT_QUANTITY_LIMIT;
    this.showSummaryPrice = this.data.showSummaryPrice === true;
    this.stockNotice = this.data.stockNotice || {};
    this.stockNoticeEnabled = this.stockNotice.enabled !== false;
    this.groupsContainer = root.querySelector('[data-vs-groups]');
    this.summaryEl = root.querySelector('[data-vs-summary]');
    this.priceEl = root.querySelector('[data-vs-price]');
    this.priceValueEl = root.querySelector('[data-vs-price-value]');
    this.footerEl = root.querySelector('.variant-selector__footer');
    this.setEl = root.querySelector('[data-vs-set]');
    this.setHeaderEl = root.querySelector('[data-vs-set-header]');
    this.setTitleEl = root.querySelector('[data-vs-set-title]');
    this.setSubtitleEl = root.querySelector('[data-vs-set-subtitle]');
    this.bikiniNoticeEl = root.querySelector('[data-vs-bikini-notice]');
    this.groupMessageEl = root.querySelector('[data-vs-group-message]');
    this.errorEl = root.querySelector('[data-vs-error]');
    this.variantIdInput = root.querySelector('[data-vs-variant-id]');
    this.restockSelection = null;
    this.availabilityEl = document.querySelector('[data-vs-availability]');
    this.moneyFormat = this.data.moneyFormat || '${{amount}}';
    this.variants = this.data.variants || [];
    this.options = this.data.options || [];
    this.optionMetaByPosition = {};
    this.options.forEach(function (option) {
      var valueMeta = {};
      (option.values || []).forEach(function (meta) {
        valueMeta[meta.value] = meta;
      });
      this.optionMetaByPosition[option.position] = {
        isColor: !!option.isColor,
        valueMeta: valueMeta,
      };
    }.bind(this));
    this.productId = this.data.productId || null;
    this.productTitle = root.getAttribute('data-product-title') || '';
    this.selectionCopy = this.data.selectionCopy || {};
    this.emptySummaryText = root.getAttribute('data-empty-summary') || this.copy('none', 'No items selected');

    this.addButton = document.getElementById('addToCartAjax');
    this.addLabel = document.getElementById('addToCartLabel');
    this.oosLabel = document.getElementById('OOSLabel');

    this.productPagePriceEl = document.querySelector('.product-page__price');
    this.productPageCompareEl = document.querySelector('.product-compare-price');
    this.productPageInstallmentEl = document.querySelector('.product-page__installments .text-highlight');
    this.activeVariantId = null;

    this.cards = [];
    this.defaultQuantity = 1;
    this.isLoading = false;
    this.errorTimeout = null;

    this.buildModel();
    this.render();
    this.updateGroupMessage();
    this.bindAddToCart();
    this.initActiveVariant();
    this.refreshSummary();
    this.clearInitialLoading();
    if (this.cards[0]) this.warmQuantityRows(this.cards[0]);
  }

  VariantSelector.prototype.clearInitialLoading = function () {
    if (this.root) {
      setClassIfChanged(this.root, 'variant-selector--initial-loading', false);
      this.root.setAttribute('aria-busy', 'false');
    }
  };

  VariantSelector.prototype.quantityLimitMessage = function () {
    if (this.quantityLimit === 1) {
      return 'You can only add 1 item at a time.';
    }
    return 'You can add up to ' + this.quantityLimit + ' items at a time.';
  };

  VariantSelector.prototype.panelId = function (group) {
    return groupElementId(this.uidPrefix, group.id) + '-panel';
  };

  VariantSelector.prototype.titleId = function (group) {
    return groupElementId(this.uidPrefix, group.id) + '-title';
  };

  VariantSelector.prototype.swatchLabel = function (group, entry, value, available) {
    var display = entry.isGroupOption
      ? previewSizeLabel(value, group.groupKey)
      : value;
    var parts = [];
    if (group.label) parts.push(group.label);
    if (entry.option && entry.option.name) parts.push(this.optionLabel(entry.option));
    parts.push(display);
    if (available === false) parts.push('sold out');
    return parts.join(', ');
  };

  VariantSelector.prototype.bindSwatchKeyboard = function (swatchesEl) {
    swatchesEl.addEventListener('keydown', function (event) {
      if (
        event.key !== 'ArrowRight' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowDown' &&
        event.key !== 'ArrowUp'
      ) {
        return;
      }

      var buttons = Array.prototype.slice.call(swatchesEl.querySelectorAll('button')).filter(function (btn) {
        return !btn.disabled;
      });
      if (!buttons.length) return;

      var currentIndex = buttons.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      var nextIndex = currentIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % buttons.length;
      } else {
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      }

      event.preventDefault();
      buttons[nextIndex].focus();
    });
  };

  VariantSelector.prototype.createOptionField = function (entry, parentEl, legendText) {
    var fieldset = document.createElement('fieldset');
    fieldset.className = 'variant-selector-option__fieldset';

    var legend = document.createElement('legend');
    legend.className = 'variant-selector-option__legend';
    legend.textContent = legendText === entry.option.name ? this.optionLabel(entry.option) : legendText;
    fieldset.appendChild(legend);

    var optionEl = document.createElement('div');
    optionEl.className = 'variant-selector-option';
    optionEl.setAttribute('data-option-position', entry.option.position);
    if (this.isColorOption(entry.option.position)) {
      setClassIfChanged(optionEl, 'variant-selector-option--color', true);
    }
    if (legendText && (entry.option.name !== legendText)) {
      setClassIfChanged(optionEl, 'variant-selector-option--split', true);
    }

    var swatches = document.createElement('div');
    swatches.className = 'variant-selector-option__swatches';
    swatches.setAttribute('role', 'group');
    swatches.setAttribute('aria-label', legend.textContent);

    optionEl.appendChild(swatches);
    fieldset.appendChild(optionEl);
    parentEl.appendChild(fieldset);
    this.bindSwatchKeyboard(swatches);

    return { optionEl: optionEl, swatches: swatches };
  };

  VariantSelector.prototype.buildModel = function () {
    var self = this;

    this.valuesByPosition = {};
    this.options.forEach(function (option) {
      self.valuesByPosition[option.position] = [];
    });
    this.variants.forEach(function (variant) {
      variant.options.forEach(function (value, index) {
        var position = index + 1;
        var list = self.valuesByPosition[position] || (self.valuesByPosition[position] = []);
        if (list.indexOf(value) === -1) list.push(value);
      });
    });

    this.defaultVariant = null;
    if (this.variants.length === 1 && normalize(this.variants[0].options[0]) === 'default title') {
      this.defaultVariant = this.variants[0];
      this.groups = [];
      this.multiOptionPosition = null;
      return;
    }

    this.groupOption = null;
    for (var i = 0; i < this.options.length; i++) {
      var position = this.options[i].position;
      var values = this.valuesByPosition[position] || [];
      var hasKeyword = values.some(function (value) {
        return detectGroupKey(value) !== null;
      });
      if (hasKeyword) {
        this.groupOption = this.options[i];
        break;
      }
    }

    this.multiOptionPosition = this.groupOption
      ? this.groupOption.position
      : this.findSizeOptionPosition();

    this.groups = this.buildGroups();
    this.isSingleGroup = this.groups.length === 1;
    this.gradeSchoolLayout = this.isGradeSchoolProduct();
  };

  VariantSelector.prototype.isGradeSchoolProduct = function () {
    if (this.groupOption || !this.isSingleGroup || this.defaultVariant || this.options.length !== 2) return false;
    var hasSize = this.options.some(function (option) { return /size/i.test(option.name); });
    return this.getGradeOptionPosition() != null && hasSize;
  };

  VariantSelector.prototype.getGradeOptionPosition = function () {
    for (var i = 0; i < this.options.length; i++) {
      if (/^grade$/i.test(this.options[i].name)) return this.options[i].position;
    }
    if (this.data.gradeSchoolLayout === true) {
      for (var j = 0; j < this.options.length; j++) {
        if (/^type$/i.test(this.options[j].name)) return this.options[j].position;
      }
    }
    return null;
  };

  VariantSelector.prototype.hasGradeSelected = function (card) {
    var gradePosition = this.getGradeOptionPosition();
    return gradePosition != null && card.sharedSelections[gradePosition] != null;
  };

  VariantSelector.prototype.setGradeSizeStepExpanded = function (card, expanded) {
    var step = card.gradeSizeStep;
    if (!step) return;
    if (expanded && step.headerEl.disabled) return;

    if (step.expanded !== expanded) {
      if (step.finishTransition) step.finishTransition();
      var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (step.bodyEl && !reducedMotion) {
        var transitionTimeout;
        var finishTransition = function (event) {
          if (event && (event.target !== step.bodyEl || event.propertyName !== 'grid-template-rows')) return;
          if (step.finishTransition !== finishTransition) return;
          if (event && step.bodyEl.getAnimations && step.bodyEl.getAnimations().some(function (animation) {
            return animation.playState === 'running' || animation.pending;
          })) return;
          window.clearTimeout(transitionTimeout);
          step.bodyEl.removeEventListener('transitionend', finishTransition);
          setClassIfChanged(step.stepEl, 'variant-selector-grade-step--animating', false);
          step.finishTransition = null;
        };
        step.finishTransition = finishTransition;
        setClassIfChanged(step.stepEl, 'variant-selector-grade-step--animating', true);
        step.bodyEl.addEventListener('transitionend', finishTransition);
        transitionTimeout = window.setTimeout(finishTransition, 360);
      }
    }

    step.expanded = expanded;
    step.stepEl.classList.toggle('variant-selector-grade-step--collapsed', !expanded);
    step.stepEl.classList.toggle('variant-selector-grade-step--expanded', expanded);
    step.headerEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (step.bodyEl) {
      if ('inert' in step.bodyEl) {
        step.bodyEl.inert = !expanded;
      } else {
        step.bodyEl.querySelectorAll('button').forEach(function (control) {
          if (!expanded) {
            control.setAttribute('tabindex', '-1');
          } else {
            control.removeAttribute('tabindex');
          }
        });
      }
    }
  };

  VariantSelector.prototype.updateGradeSizeStepState = function (card) {
    var step = card.gradeSizeStep;
    if (!step) return;

    var hasGrade = this.hasGradeSelected(card);
    step.headerEl.disabled = !hasGrade;
    step.stepEl.classList.toggle('variant-selector-grade-step--locked', !hasGrade);

    if (!hasGrade) {
      this.setGradeSizeStepExpanded(card, false);
    }
  };

  VariantSelector.prototype.buildGradeSchoolOptions = function (card, group, cardOptions, optionsWrap) {
    var self = this;
    var labels = (this.data.gradeSchoolLabels) || {};
    var gradeEntry = null;
    var sizeEntry = null;

    cardOptions.forEach(function (entry) {
      if (!entry.values.length) return;
      if (self.isMultiOption(entry.option.position)) {
        sizeEntry = entry;
      } else if (entry.option.position === self.getGradeOptionPosition()) {
        gradeEntry = entry;
      }
    });

    if (!gradeEntry || !sizeEntry) return;

    setClassIfChanged(card.el, 'variant-selector-group--grade-school', true);

    var stepsWrap = document.createElement('div');
    stepsWrap.className = 'variant-selector-grade-steps';

    var step1 = document.createElement('div');
    step1.className = 'variant-selector-grade-step variant-selector-grade-step--type';
    var step1Title = document.createElement('p');
    step1Title.className = 'variant-selector-grade-step__title';
    step1Title.textContent = labels.gradeStep || 'Step 1: Select a Grade';
    step1.appendChild(step1Title);
    self.appendOptionSwatches(card, group, gradeEntry, step1);
    stepsWrap.appendChild(step1);

    var sizeStepId = groupElementId(this.uidPrefix, group.id) + '-grade-size';
    var step2 = document.createElement('div');
    step2.className =
      'variant-selector-grade-step variant-selector-grade-step--size variant-selector-grade-step--collapsed variant-selector-grade-step--locked';

    var sizeHeader = document.createElement('button');
    sizeHeader.type = 'button';
    sizeHeader.className = 'variant-selector-grade-step__header';
    sizeHeader.setAttribute('aria-expanded', 'false');
    sizeHeader.setAttribute('aria-controls', sizeStepId + '-body');
    sizeHeader.disabled = true;
    sizeHeader.textContent = labels.step2 || 'Step 2: Select a Size';

    var sizeBody = document.createElement('div');
    sizeBody.id = sizeStepId + '-body';
    sizeBody.className = 'variant-selector-grade-step__body';
    if ('inert' in sizeBody) {
      sizeBody.inert = true;
    }

    var sizeBodyInner = document.createElement('div');
    sizeBodyInner.className = 'variant-selector-grade-step__body-inner';
    self.appendOptionSwatches(card, group, sizeEntry, sizeBodyInner);

    sizeBody.appendChild(sizeBodyInner);
    step2.appendChild(sizeHeader);
    step2.appendChild(sizeBody);

    card.gradeSizeStep = {
      stepEl: step2,
      headerEl: sizeHeader,
      bodyEl: sizeBody,
      expanded: false,
    };

    sizeHeader.addEventListener('click', function () {
      if (sizeHeader.disabled) return;
      self.setGradeSizeStepExpanded(card, !card.gradeSizeStep.expanded);
    });

    stepsWrap.appendChild(step2);
    optionsWrap.appendChild(stepsWrap);
    this.updateGradeSizeStepState(card);
  };

  VariantSelector.prototype.appendOptionSwatches = function (card, group, entry, parentEl) {
    var self = this;
    var field = this.createOptionField(entry, parentEl, entry.option.name);
    var swatches = field.swatches;
    var buttons = [];

    entry.values.forEach(function (value) {
      var button = self.createSwatchButton(card, group, entry, value);
      swatches.appendChild(button);
      buttons.push(button);
    });

    card.buttonsByPosition[entry.option.position] = buttons;
    return field;
  };

  VariantSelector.prototype.findSizeOptionPosition = function () {
    for (var i = 0; i < this.options.length; i++) {
      if (/size/i.test(this.options[i].name)) return this.options[i].position;
    }
    return this.options.length ? this.options[this.options.length - 1].position : null;
  };

  VariantSelector.prototype.isMultiOption = function (position) {
    return this.multiOptionPosition != null && position === this.multiOptionPosition;
  };

  VariantSelector.prototype.buildGroups = function () {
    var self = this;

    if (!this.groupOption) {
      return [{ id: 'all', label: '', groupKey: null, sizeValues: null }];
    }

    var position = this.groupOption.position;
    var values = this.valuesByPosition[position] || [];
    var buckets = {};
    var order = [];

    values.forEach(function (value) {
      var def = detectGroupKey(value);
      var key = def ? def.key : 'OTHER';
      var label = def ? def.label : self.groupOption.name;
      if (!buckets[key]) {
        buckets[key] = { id: key.toLowerCase(), label: label, groupKey: def ? def.key : null, sizeValues: [] };
        order.push(key);
      }
      buckets[key].sizeValues.push(value);
    });

    var ordered = [];
    GROUP_DEFS.forEach(function (def) {
      if (buckets[def.key]) ordered.push(buckets[def.key]);
    });
    order.forEach(function (key) {
      if (key === 'OTHER' && buckets[key]) ordered.push(buckets[key]);
    });

    return ordered;
  };

  VariantSelector.prototype.variantsForGroup = function (group) {
    if (!group.sizeValues || !this.groupOption) return this.variants.slice();
    var position = this.groupOption.position;
    var allowed = group.sizeValues.map(normalize);
    return this.variants.filter(function (variant) {
      return allowed.indexOf(normalize(variant.options[position - 1])) !== -1;
    });
  };

  VariantSelector.prototype.cardOptions = function (group) {
    var self = this;
    return this.options.map(function (option) {
      var isGroupOption = self.groupOption && option.position === self.groupOption.position;
      var values;
      if (isGroupOption && group.sizeValues) {
        values = group.sizeValues.slice();
      } else {
        values = (self.valuesByPosition[option.position] || []).slice();
      }
      return { option: option, isGroupOption: isGroupOption, values: values };
    });
  };

  VariantSelector.prototype.shouldSplitTopsBottoms = function (group, values) {
    if (!group.groupKey || (group.groupKey !== 'MOM' && group.groupKey !== 'ADULT')) {
      return false;
    }
    var hasTops = false;
    var hasBottoms = false;
    values.forEach(function (value) {
      if (isTopSize(value)) hasTops = true;
      if (isBottomSize(value)) hasBottoms = true;
    });
    return hasTops && hasBottoms;
  };

  VariantSelector.prototype.isFamilySet = function () {
    if (!this.groupOption || this.groups.length < 4) return false;
    if (this.data && this.data.isMysteryBox) return false;
    return true;
  };

  VariantSelector.prototype.isMatchingSetProduct = function () {
    if (this.data && this.data.isMysteryBox) return false;
    return !!(this.groupOption && this.groups.length >= 2);
  };

  VariantSelector.prototype.isGroupActive = function (card) {
    if (this.isSingleGroup) return true;
    return card.el.classList.contains('variant-selector-group--expanded');
  };

  VariantSelector.prototype.clearGroupSelections = function (card) {
    if (this.restockSelection && this.restockSelection.card === card) {
      this.restockSelection = null;
    }
    card.quantityByValue = {};
    card.sharedSelections = {};
  };

  VariantSelector.prototype.copy = function (key, fallback, values) {
    var value = this.selectionCopy[key] || fallback;
    Object.keys(values || {}).forEach(function (name) {
      value = value.split('__' + name + '__').join(String(values[name]));
    });
    return value;
  };

  VariantSelector.prototype.optionLabel = function (option) {
    if (this.data.isGiftCard && /^type$/i.test(option.name)) return this.copy('amount', 'Amount');
    if (/^grade$/i.test(option.name) || (this.gradeSchoolLayout && /^type$/i.test(option.name))) return this.copy('grade', 'Grade');
    return /^title$/i.test(option.name) ? this.copy('option', 'Option') : option.name;
  };

  VariantSelector.prototype.optionList = function (options) {
    var self = this;
    var names = options.map(function (option) { return self.optionLabel(option).toLowerCase(); });
    if (names.length < 2) return names.join('');
    return names.slice(0, -1).join(', ') + ' ' + this.copy('and', 'and') + ' ' + names[names.length - 1];
  };

  VariantSelector.prototype.missingOptions = function (card, sharedOnly) {
    var self = this;
    return this.options.filter(function (option) {
      if (!card.buttonsByPosition[option.position]) return false;
      if (self.isMultiOption(option.position)) {
        return !sharedOnly && !self.getSelectedMultiValues(card).length;
      }
      return card.sharedSelections[option.position] == null;
    });
  };

  VariantSelector.prototype.emptyButtonLabel = function () {
    var self = this;
    var active = this.cards.filter(function (card) { return self.isGroupActive(card); });
    if (!active.length && this.groups.length > 1) return this.copy('chooseFamily', 'Choose a family member');
    var missing = null;
    active.forEach(function (card) {
      var options = self.missingOptions(card, false);
      if (options.length && (!missing || options.length < missing.length)) missing = options;
    });
    return missing ? this.copy('choose', 'Choose your __options__', { options: this.optionList(missing) })
      : this.copy('chooseOptions', 'Choose your options');
  };

  VariantSelector.prototype.getMissingSharedSelectionMessage = function (card) {
    var missing = this.missingOptions(card, true);
    return missing.length ? this.copy('chooseFirst', 'Choose your __options__ first.', { options: this.optionList(missing) })
      : this.copy('chooseOptions', 'Choose your options');
  };

  VariantSelector.prototype.selectionLabel = function (card, variant) {
    var self = this;
    return this.options.map(function (option) {
      var value = variant.options[option.position - 1];
      if (self.groupOption && option.position === self.groupOption.position) value = cleanSizeLabel(value, card.group.groupKey) || value;
      return self.optionLabel(option) + ': ' + value;
    }).join(' · ');
  };

  VariantSelector.prototype.hasRequiredSharedSelections = function (card) {
    var self = this;
    return Object.keys(card.buttonsByPosition).every(function (positionKey) {
      var position = Number(positionKey);
      if (self.isMultiOption(position)) return true;
      return card.sharedSelections[position] != null;
    });
  };

  VariantSelector.prototype.getGroupMessage = function () {
    if (!this.groups.length) return '';
    var self = this;
    var shared = this.options.filter(function (option) { return !self.isMultiOption(option.position); });
    var multi = this.options.filter(function (option) { return self.isMultiOption(option.position); });
    var ordered = shared.concat(multi);
    var multiName = multi.length === 1 ? this.optionLabel(multi[0]).toLowerCase() : '';
    var pluralKeys = { size: 'pluralSize', color: 'pluralColor', colour: 'pluralColour', type: 'pluralType', style: 'pluralStyle' };
    var pluralFallbacks = { size: 'sizes', color: 'colors', colour: 'colours', type: 'types', style: 'styles' };
    var plural = pluralKeys[multiName] ? this.copy(pluralKeys[multiName], pluralFallbacks[multiName]) : '';
    var restricted = this.quantityLimit === RESTRICTED_QUANTITY_LIMIT;
    var family = this.groupOption && this.groups.length > 1;
    var message;
    if (family) {
      if (!restricted && !shared.length && multiName === 'size') {
        message = this.copy('familySizes', 'Choose family members, then select your sizes.');
      } else {
        message = this.copy('familyIntro', 'Choose family members, then __options__.', { options: this.optionList(ordered) });
        if (restricted) message += ' ' + this.copy('oneAtATime', 'One item at a time.');
        else if (multiName === 'size') message += ' ' + this.copy('multipleSizes', 'Select multiple sizes if needed.');
      }
    } else if (this.gradeSchoolLayout) {
      message = this.copy('choose', 'Choose your __options__', { options: this.optionList(ordered) }) + '.';
    } else if (restricted) {
      message = this.copy('instructionSingle', 'Choose your __options__. One item at a time.', { options: this.optionList(ordered) });
    } else if (shared.length && multi.length) {
      message = this.copy('instructionShared', 'Choose __shared__, then select your __option__.', {
        shared: this.optionList(shared), option: plural || this.optionList(multi)
      });
    } else if (plural) {
      message = this.copy('instructionMulti', 'Choose one or more __options__.', { options: plural });
    } else {
      message = this.copy('choose', 'Choose your __options__', { options: this.optionList(ordered) }) + '.';
    }
    var messages = (this.data && this.data.messages) || {};
    if (this.data.isMysteryBox && messages.mysteryBox) {
      var detail = /^Please read product description below for all the details\.?$/i.test(messages.mysteryBox.trim())
        ? this.copy('mysteryDetails', 'See the description for mystery box details.') : messages.mysteryBox;
      message += ' ' + detail;
    }
    return message;
  };

  VariantSelector.prototype.updateGroupMessage = function () {
    var messages = (this.data && this.data.messages) || {};
    var isFamilySet = this.isFamilySet();

    if (this.setEl) {
      this.setEl.classList.toggle('variant-selector__set--family', isFamilySet);
    }

    if (isFamilySet) {
      var count = this.groups.length;

      if (this.setHeaderEl) this.setHeaderEl.hidden = false;
      if (this.setTitleEl) {
        this.setTitleEl.textContent = messages.matchingSetTitle || 'Build your matching set';
      }
      if (this.setSubtitleEl) {
        this.setSubtitleEl.textContent = this.copy('familySubtitle', 'Choose only the family members you need.');
      }
      if (this.bikiniNoticeEl) {
        this.bikiniNoticeEl.hidden = !this.bikiniNoticeEl.textContent.trim();
      }
      if (this.groupMessageEl) {
        var familyMessage = this.getGroupMessage();
        this.groupMessageEl.textContent = familyMessage;
        this.groupMessageEl.hidden = !familyMessage;
      }
      return;
    }

    if (this.setHeaderEl) this.setHeaderEl.hidden = true;
    if (this.bikiniNoticeEl) {
      this.bikiniNoticeEl.hidden = !this.bikiniNoticeEl.textContent.trim();
    }

    if (!this.groupMessageEl) return;

    var message = this.getGroupMessage();
    if (message) {
      this.groupMessageEl.textContent = message;
      this.groupMessageEl.hidden = false;
    } else {
      this.groupMessageEl.textContent = '';
      this.groupMessageEl.hidden = true;
    }
  };

  VariantSelector.prototype.isColorOption = function (position) {
    var meta = this.optionMetaByPosition[position];
    return !!(meta && meta.isColor);
  };

  VariantSelector.prototype.createSwatchButton = function (card, group, entry, value) {
    var self = this;
    var optionMeta = this.optionMetaByPosition[entry.option.position];
    var button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('data-value', value);
    button.setAttribute('data-option-position', String(entry.option.position));
    button.setAttribute('aria-pressed', 'false');

    if (optionMeta && optionMeta.isColor) {
      var valueMeta = optionMeta.valueMeta[value] || {};
      button.className = 'variant-selector-color-swatch';

      var chip = document.createElement('span');
      chip.className = 'variant-selector-color-swatch__chip';
      chip.setAttribute('aria-hidden', 'true');
      if (valueMeta.backgroundColor) {
        chip.style.backgroundColor = valueMeta.backgroundColor;
      }
      if (valueMeta.backgroundImage) {
        chip.style.backgroundImage = 'url(' + valueMeta.backgroundImage + ')';
      }
      button.appendChild(chip);
    } else {
      button.className = 'variant-selector-swatch' + (entry.isGroupOption ? ' variant-selector-swatch--size' : '');
      button.textContent = entry.isGroupOption
        ? previewSizeLabel(value, group.groupKey)
        : value;
    }

    button.setAttribute('aria-label', this.swatchLabel(group, entry, value, true));

    button.addEventListener('click', function () {
      self.onSelect(card, entry.option.position, value);
    });

    return button;
  };

  VariantSelector.prototype.appendSwatches = function (card, group, entry, values, parentEl, headingText) {
    var self = this;
    var legendText = headingText || entry.option.name;
    var field = this.createOptionField(entry, parentEl, legendText);
    var swatches = field.swatches;

    if (!card.buttonsByPosition[entry.option.position]) {
      card.buttonsByPosition[entry.option.position] = [];
    }

    values.forEach(function (value) {
      var button = self.createSwatchButton(card, group, entry, value);
      swatches.appendChild(button);
      card.buttonsByPosition[entry.option.position].push(button);
    });
  };

  VariantSelector.prototype.render = function () {
    var self = this;
    if (!this.groupsContainer) return;
    this.groupsContainer.innerHTML = '';

    this.groups.forEach(function (group) {
      var card = self.buildCard(group);
      self.cards.push(card);
      self.groupsContainer.appendChild(card.el);
    });

    if (this.gradeSchoolLayout && this.root) {
      setClassIfChanged(this.root, 'variant-selector--grade-school', true);
    }

    this.groupsContainer.setAttribute('data-group-count', String(this.cards.length));
  };

  VariantSelector.prototype.buildCard = function (group) {
    var self = this;
    var groupVariants = this.variantsForGroup(group);
    var cardOptions = this.cardOptions(group);
    var isSingleGroup = this.isSingleGroup;

    var el = document.createElement('div');
    el.className = 'variant-selector-group' + (isSingleGroup ? ' variant-selector-group--single' : '');
    el.setAttribute('data-group-id', group.id);

    var header = null;
    var price = null;
    var subtitle = null;
    var badge = null;
    var defaultSubtitle = this.groupSubtitle(group);
    var panelId = this.panelId(group);
    var titleId = this.titleId(group);

    if (!isSingleGroup) {
      header = document.createElement('button');
      header.type = 'button';
      header.className = 'variant-selector-group__header';
      header.setAttribute('aria-expanded', 'false');
      header.setAttribute('aria-controls', panelId);

      var checkboxVisual = document.createElement('span');
      checkboxVisual.className = 'variant-selector-group__checkbox';
      checkboxVisual.setAttribute('aria-hidden', 'true');

      var heading = document.createElement('span');
      heading.className = 'variant-selector-group__heading';

      var titleRow = document.createElement('span');
      titleRow.className = 'variant-selector-group__title-row';

      var title = document.createElement('span');
      title.className = 'variant-selector-group__title';
      title.id = titleId;
      title.textContent = group.label;
      titleRow.appendChild(title);

      heading.appendChild(titleRow);

      subtitle = document.createElement('span');
      subtitle.className = 'variant-selector-group__subtitle';
      subtitle.setAttribute('aria-live', 'polite');
      setSizeSummary(subtitle, defaultSubtitle, ' - ');
      heading.appendChild(subtitle);

      var meta = document.createElement('span');
      meta.className = 'variant-selector-group__meta';

      badge = document.createElement('span');
      badge.className = 'variant-selector-group__badge';
      badge.setAttribute('aria-live', 'polite');
      badge.textContent = '0 items';
      badge.hidden = true;

      price = document.createElement('span');
      price.className = 'variant-selector-group__price';
      price.setAttribute('aria-live', 'polite');
      meta.appendChild(price);
      meta.appendChild(badge);

      header.appendChild(checkboxVisual);
      header.appendChild(heading);
      header.appendChild(meta);
      el.appendChild(header);
    }

    var body = document.createElement('div');
    body.className = 'variant-selector-group__body';
    body.id = panelId;
    body.setAttribute('role', 'region');
    if (isSingleGroup) {
      body.setAttribute('aria-label', group.label || 'Product options');
    } else {
      body.setAttribute('aria-labelledby', titleId);
    }
    if (isSingleGroup) {
      body.hidden = false;
      body.setAttribute('aria-hidden', 'false');
    } else {
      body.setAttribute('aria-hidden', 'true');
    }

    var bodyInner = document.createElement('div');
    bodyInner.className = 'variant-selector-group__body-inner';

    var optionsWrap = document.createElement('div');
    optionsWrap.className = 'variant-selector-group__options';
    bodyInner.appendChild(optionsWrap);

    var quantitiesWrap = document.createElement('div');
    quantitiesWrap.className = 'variant-selector-group__quantities';
    bodyInner.appendChild(quantitiesWrap);

    body.appendChild(bodyInner);
    el.appendChild(body);

    var card = {
      el: el,
      group: group,
      groupVariants: groupVariants,
      priceEl: price,
      subtitleEl: subtitle,
      badgeEl: badge,
      defaultSubtitle: defaultSubtitle,
      headerEl: isSingleGroup ? null : header,
      bodyEl: body,
      quantitiesEl: quantitiesWrap,
      sharedSelections: {},
      quantityByValue: {},
      buttonsByPosition: {},
    };

    if (card.headerEl) {
      card.headerEl.addEventListener('click', function () {
        self.toggleCardExpanded(card);
      });
    }

    if (this.gradeSchoolLayout && isSingleGroup) {
      this.buildGradeSchoolOptions(card, group, cardOptions, optionsWrap);
    } else {
      cardOptions.forEach(function (entry) {
        if (!entry.values.length) return;

        if (entry.isGroupOption && self.shouldSplitTopsBottoms(group, entry.values)) {
          var topValues = entry.values.filter(isTopSize);
          var bottomValues = entry.values.filter(isBottomSize);
          self.appendSwatches(
            card,
            group,
            entry,
            topValues,
            optionsWrap,
            self.data.swimsuitTopSubheading || 'TOPS ONLY'
          );
          self.appendSwatches(
            card,
            group,
            entry,
            bottomValues,
            optionsWrap,
            self.data.swimsuitBottomSubheading || 'BOTTOMS ONLY'
          );
          return;
        }

        self.appendOptionSwatches(card, group, entry, optionsWrap);
      });
    }

    this.updateAvailability(card);
    this.updateCardState(card);
    if (!isSingleGroup) {
      this.setCardExpanded(card, false);
    }
    return card;
  };

  VariantSelector.prototype.toggleCardExpanded = function (card) {
    if (this.isSingleGroup) return;
    var expanded = !card.el.classList.contains('variant-selector-group--expanded');
    this.setCardExpanded(card, expanded);
    if (expanded) {
      this.warmQuantityRows(card);
      this.updateAvailability(card);
    }
    this.refreshSummary();
  };

  VariantSelector.prototype.setCardExpanded = function (card, expanded) {
    if (this.isSingleGroup) return;
    var wasExpanded = card.el.classList.contains('variant-selector-group--expanded');
    if (wasExpanded !== expanded) {
      if (card.finishTransition) card.finishTransition();
      var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (card.bodyEl && !reducedMotion) {
        var transitionTimeout;
        var finishTransition = function (event) {
          if (event && (event.target !== card.bodyEl || event.propertyName !== 'grid-template-rows')) return;
          if (card.finishTransition !== finishTransition) return;
          if (event && card.bodyEl.getAnimations && card.bodyEl.getAnimations().some(function (animation) {
            return animation.playState === 'running' || animation.pending;
          })) return;
          window.clearTimeout(transitionTimeout);
          card.bodyEl.removeEventListener('transitionend', finishTransition);
          setClassIfChanged(card.el, 'variant-selector-group--animating', false);
          card.finishTransition = null;
        };
        card.finishTransition = finishTransition;
        setClassIfChanged(card.el, 'variant-selector-group--animating', true);
        card.bodyEl.addEventListener('transitionend', finishTransition);
        transitionTimeout = window.setTimeout(finishTransition, 360);
      }
    }
    if (!expanded) {
      this.clearGroupSelections(card);
    }
    if (card.headerEl) {
      card.headerEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
    if (card.bodyEl) {
      card.bodyEl.setAttribute('aria-hidden', expanded ? 'false' : 'true');
      if ('inert' in card.bodyEl) {
        card.bodyEl.inert = !expanded;
      } else {
        card.bodyEl.querySelectorAll('button').forEach(function (control) {
          if (!expanded) {
            control.setAttribute('tabindex', '-1');
          } else {
            control.removeAttribute('tabindex');
          }
        });
      }
    }
    card.el.classList.toggle('variant-selector-group--expanded', expanded);
  };

  VariantSelector.prototype.groupSubtitle = function (group) {
    if (!group.sizeValues || !group.sizeValues.length) return [];
    var labels = group.sizeValues.map(function (value) {
      return previewSizeLabel(value, group.groupKey);
    }).filter(function (label) {
      return !isAccessoryLabel(label);
    });
    labels.sort(function (a, b) {
      return sizeRank(a) - sizeRank(b);
    });
    if (labels.length < 2) return labels;
    return [labels[0], labels[labels.length - 1]];
  };

  VariantSelector.prototype.onSelect = function (card, position, value) {
    if (this.isMultiOption(position)) {
      this.toggleSizeSelection(card, value);
      return;
    }

    this.restockSelection = null;

    if (this.data.singleVariantLimit) {
      card.quantityByValue = {};
    }

    if (card.sharedSelections[position] === value) {
      delete card.sharedSelections[position];
    } else {
      card.sharedSelections[position] = value;
    }

    this.pruneInvalidSelections(card);
    this.updateAvailability(card);

    if (this.gradeSchoolLayout && card.gradeSizeStep) {
      this.updateGradeSizeStepState(card);
      if (!this.isMultiOption(position) && card.sharedSelections[position]) {
        this.setGradeSizeStepExpanded(card, true);
      }
    }

    var variant = this.resolvePreviewVariant(card);
    if (variant) {
      this.trackActiveVariant(variant);
    } else {
      this.syncActiveVariant();
    }

    this.refreshSummary();

    if (variant && variant.image) {
      this.switchGalleryImage(variant.image);
    }
  };

  VariantSelector.prototype.toggleSizeSelection = function (card, value) {
    if (this.hasRequiredSharedSelections(card)) {
      var restockVariant = this.resolveVariantForValue(card, value);
      if (restockVariant && !restockVariant.available) {
        var isSameRestockSelection = this.restockSelection &&
          this.restockSelection.card === card && this.restockSelection.value === value;
        this.restockSelection = isSameRestockSelection ? null : { card: card, value: value };
        if (this.restockSelection) this.trackActiveVariant(restockVariant);
        else this.syncActiveVariant();
        this.scheduleSelectionRender();
        if (this.restockSelection && restockVariant.image) {
          this.switchGalleryImage(restockVariant.image);
        }
        return;
      }
    }

    this.restockSelection = null;
    if (card.quantityByValue[value] != null) {
      delete card.quantityByValue[value];
    } else {
      if (!this.hasRequiredSharedSelections(card)) {
        this.showError(this.getMissingSharedSelectionMessage(card));
        return;
      }
      if (this.data.singleVariantLimit) {
        card.quantityByValue = {};
      } else if (this.totalQuantity() >= this.quantityLimit) {
        this.showError(this.quantityLimitMessage());
        return;
      }
      card.quantityByValue[value] = 1;
    }

    this.updateAvailability(card);

    var variant = card.quantityByValue[value] != null
      ? this.resolveVariantForValue(card, value)
      : null;
    if (variant) {
      this.trackActiveVariant(variant);
    } else {
      var preview = this.resolvePreviewVariant(card);
      if (preview) this.trackActiveVariant(preview);
      else this.syncActiveVariant();
    }

    this.scheduleSelectionRender();

    if (variant && variant.image) {
      this.switchGalleryImage(variant.image);
    }
  };

  VariantSelector.prototype.pruneInvalidSelections = function (card) {
    var self = this;
    Object.keys(card.quantityByValue).forEach(function (value) {
      var variant = self.resolveVariantForValue(card, value);
      if (!variant || !variant.available) {
        delete card.quantityByValue[value];
      }
    });
  };

  VariantSelector.prototype.buildSelections = function (card, multiValue) {
    var selections = {};

    Object.keys(card.sharedSelections).forEach(function (positionKey) {
      selections[Number(positionKey)] = card.sharedSelections[positionKey];
    });

    if (multiValue != null && this.multiOptionPosition != null) {
      selections[this.multiOptionPosition] = multiValue;
    }

    return selections;
  };

  VariantSelector.prototype.resolveVariantForValue = function (card, multiValue) {
    if (multiValue == null) return null;

    var selections = this.buildSelections(card, multiValue);
    var positions = Object.keys(card.buttonsByPosition).map(Number);
    var complete = positions.every(function (position) {
      return selections[position] != null;
    });
    if (!complete) return null;

    if (!card.variantLookup) {
      card.variantLookup = new Map();
      card.groupVariants.forEach(function (variant) {
        var key = JSON.stringify(positions.map(function (position) {
          return normalize(variant.options[position - 1]);
        }));
        var existing = card.variantLookup.get(key);
        if (!existing || (!existing.available && variant.available)) {
          card.variantLookup.set(key, variant);
        }
      });
    }
    var key = JSON.stringify(positions.map(function (position) {
      return normalize(selections[position]);
    }));
    return card.variantLookup.get(key) || null;
  };

  VariantSelector.prototype.resolvePreviewVariant = function (card) {
    var readyItems = this.cardReadyItems(card);
    if (readyItems.length) return readyItems[0].variant;

    var pool = this.getDisplayPricePool(card);
    if (pool.length === 1) return pool[0];
    return null;
  };

  VariantSelector.prototype.updateAvailability = function (card) {
    var availabilityKey = JSON.stringify(card.sharedSelections);
    if (card.availabilityKey === availabilityKey) return;
    card.availabilityKey = availabilityKey;
    var self = this;
    Object.keys(card.buttonsByPosition).forEach(function (positionKey) {
      var position = Number(positionKey);
      var isMulti = self.isMultiOption(position);

      card.buttonsByPosition[position].forEach(function (button) {
        var value = button.getAttribute('data-value');
        var probeSelections = isMulti
          ? self.buildSelections(card, value)
          : Object.assign({}, card.sharedSelections, (function () {
              var next = {};
              next[position] = value;
              return next;
            })());

        var matchingVariants = card.groupVariants.filter(function (variant) {
          return Object.keys(probeSelections).every(function (selKey) {
            var selPosition = Number(selKey);
            return normalize(variant.options[selPosition - 1]) === normalize(probeSelections[selPosition]);
          });
        });
        var available = matchingVariants.some(function (variant) {
          return variant.available;
        });

        var entry = {
          option: self.options.filter(function (opt) {
            return opt.position === position;
          })[0],
          isGroupOption: isMulti,
        };

        var soldoutClass = button.classList.contains('variant-selector-color-swatch')
          ? 'variant-selector-color-swatch--soldout'
          : 'variant-selector-swatch--soldout';
        button.classList.toggle(soldoutClass, !available);
        button.disabled = matchingVariants.length === 0;
        button.setAttribute('aria-label', self.swatchLabel(card.group, entry, value, available));
      });
    });
  };

  VariantSelector.prototype.getSelectedMultiValues = function (card) {
    return Object.keys(card.quantityByValue).sort(function (a, b) {
      var labelA = this.groupOption
        ? cleanSizeLabel(a, card.group.groupKey)
        : a;
      var labelB = this.groupOption
        ? cleanSizeLabel(b, card.group.groupKey)
        : b;
      return sizeRank(labelA) - sizeRank(labelB);
    }.bind(this));
  };


  VariantSelector.prototype.scheduleQuantityRows = function (card) {
    var self = this;
    if (!this.pendingQuantityCards) this.pendingQuantityCards = new Set();
    this.pendingQuantityCards.add(card);
    if (this.quantityRowsScheduled) return;
    this.quantityRowsScheduled = true;
    var flush = function () { self.flushQuantityRows(); };
    // Selection, price and cart state stay synchronous. Build the secondary
    // quantity controls after a paint, with a timer for hidden/throttled tabs.
    this.quantityRowsTimer = window.setTimeout(flush, 100);
    if (document.visibilityState !== 'hidden') {
      this.quantityRowsFrame = window.requestAnimationFrame(function () {
        self.quantityRowsFrame = window.requestAnimationFrame(flush);
      });
    }
  };

  VariantSelector.prototype.flushQuantityRows = function () {
    if (!this.quantityRowsScheduled) return;
    this.quantityRowsScheduled = false;
    window.clearTimeout(this.quantityRowsTimer);
    if (this.quantityRowsFrame) window.cancelAnimationFrame(this.quantityRowsFrame);
    this.quantityRowsFrame = null;
    var cards = Array.from(this.pendingQuantityCards);
    this.pendingQuantityCards.clear();
    if (!this.root.isConnected) return;
    var self = this;
    cards.forEach(function (card) { self.renderQuantityRows(card); });
  };

  VariantSelector.prototype.ensureQuantityRow = function (card, value, variant) {
    if (!card.quantityRows) card.quantityRows = new Map();
    if (card.quantityRows.has(value)) return card.quantityRows.get(value);
    var self = this;
    var label = this.selectionLabel(card, variant);
    var row = document.createElement('div');
    row.className = 'variant-selector-qty-row';
    row.setAttribute('data-value', value);

    var rowLabel = document.createElement('span');
    rowLabel.className = 'variant-selector-qty-row__label';
    rowLabel.textContent = label;
    row.appendChild(rowLabel);

    var qty = document.createElement('div');
    qty.className = 'variant-selector-qty';
    qty.setAttribute('role', 'group');
    qty.setAttribute('aria-label', 'Quantity for ' + label);

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'variant-selector-qty__btn';
    minus.setAttribute('aria-label', 'Decrease quantity for ' + label);
    minus.textContent = '−';
    minus.addEventListener('click', function () {
      self.changeQuantity(card, value, -1);
    });

    var count = document.createElement('span');
    count.className = 'variant-selector-qty__count';
    count.setAttribute('aria-live', 'polite');
    count.setAttribute('aria-atomic', 'true');
    count.textContent = String(card.quantityByValue[value] || 1);

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'variant-selector-qty__btn';
    plus.setAttribute('aria-label', 'Increase quantity for ' + label);
    plus.textContent = '+';
    plus.addEventListener('click', function () {
      self.changeQuantity(card, value, 1);
    });
    plus.disabled = self.totalQuantity() >= self.quantityLimit;

    qty.appendChild(minus);
    qty.appendChild(count);
    qty.appendChild(plus);
    row.appendChild(qty);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'variant-selector-qty-row__remove';
    remove.setAttribute('aria-label', 'Remove ' + label);
    remove.textContent = '×';
    remove.addEventListener('click', function () {
      delete card.quantityByValue[value];
      self.updateAvailability(card);
      self.refreshSummary();
    });
    row.appendChild(remove);


    // Attach each size's controls once. Hidden rows have no layout or tab stops.
    row.hidden = true;
    if (!Object.keys(card.quantityByValue).length) setHiddenIfChanged(card.quantitiesEl, true);
    var controls = { row: row, count: count, plus: plus, minus: minus, remove: remove, qty: qty, label: rowLabel };
    card.quantityRows.set(value, controls);
    var values = Array.from(card.quantityRows.keys()).sort(function (a, b) {
      var labelA = self.groupOption ? cleanSizeLabel(a, card.group.groupKey) : a;
      var labelB = self.groupOption ? cleanSizeLabel(b, card.group.groupKey) : b;
      return sizeRank(labelA) - sizeRank(labelB);
    });
    var next = null;
    for (var index = values.indexOf(value) + 1; index < values.length; index++) {
      var other = card.quantityRows.get(values[index]).row;
      if (other.parentNode === card.quantitiesEl) { next = other; break; }
    }
    card.quantitiesEl.insertBefore(row, next);
    return controls;
  };

  VariantSelector.prototype.warmQuantityRows = function (card) {
    if (card.quantityWarmStarted) return;
    card.quantityWarmStarted = true;
    var self = this;
    var values = [];
    Object.keys(card.buttonsByPosition).forEach(function (position) {
      if (!self.isMultiOption(Number(position))) return;
      card.buttonsByPosition[position].forEach(function (button) {
        var value = button.getAttribute('data-value');
        if (values.indexOf(value) < 0) values.push(value);
      });
    });
    // Only the primary or an opened group is warmed; unusually large option
    // sets keep the ordinary selected-size path rather than expanding startup.
    if (values.length > 24) return;
    function schedule() {
      if (window.requestIdleCallback) window.requestIdleCallback(prepare, { timeout: 3000 });
      else window.setTimeout(prepare, 180);
    }
    function prepare(deadline) {
      if (!self.root.isConnected || !values.length) return;
      if (document.visibilityState === 'hidden' ||
          (navigator.scheduling && navigator.scheduling.isInputPending && navigator.scheduling.isInputPending())) {
        schedule(); return;
      }
      var count = 0;
      while (values.length && count++ < 2) {
        var value = values.shift();
        var variant = self.resolveVariantForValue(card, value);
        if (variant) self.ensureQuantityRow(card, value, variant);
        if (deadline && deadline.timeRemaining && deadline.timeRemaining() < 2) break;
      }
      if (values.length) schedule();
    }
    schedule();
  };

  VariantSelector.prototype.renderQuantityRows = function (card) {
    var self = this;
    if (!card.quantityRows) card.quantityRows = new Map();
    var selectedValues = this.getSelectedMultiValues(card);
    var retainedValues = new Set();
    selectedValues.forEach(function (value) {
      var variant = self.resolveVariantForValue(card, value);
      if (!variant) return;
      retainedValues.add(value);
      var controls = self.ensureQuantityRow(card, value, variant);
      var label = self.selectionLabel(card, variant);
      if (controls.label.textContent !== label) {
        controls.label.textContent = label;
        controls.qty.setAttribute('aria-label', 'Quantity for ' + label);
        controls.minus.setAttribute('aria-label', 'Decrease quantity for ' + label);
        controls.plus.setAttribute('aria-label', 'Increase quantity for ' + label);
        controls.remove.setAttribute('aria-label', 'Remove ' + label);
      }
      setTextIfChanged(controls.count, card.quantityByValue[value]);
      if (controls.row.hidden) {
        controls.row.hidden = false;
      }
    });
    card.quantityRows.forEach(function (controls, value) {
      if (!retainedValues.has(value) && !controls.row.hidden) {
        controls.row.hidden = true;
      }
    });
    // Preserve the selector's existing stable order, including sizes with equal
    // ranks (for example numeric children's sizes selected in either order).
    var visibleRows = Array.from(card.quantitiesEl.children).filter(function (row) { return !row.hidden; });
    var orderedRows = selectedValues.filter(function (value) { return retainedValues.has(value); }).map(function (value) { return card.quantityRows.get(value).row; });
    orderedRows.forEach(function (row, index) {
      if (visibleRows[index] === row) return;
      card.quantitiesEl.insertBefore(row, visibleRows[index] || null);
      visibleRows.splice(visibleRows.indexOf(row), 1);
      visibleRows.splice(index, 0, row);
    });
    setHiddenIfChanged(card.quantitiesEl, retainedValues.size === 0);
  };

  VariantSelector.prototype.changeQuantity = function (card, value, delta) {
    if (card.quantityByValue[value] == null) return;
    var current = card.quantityByValue[value];
    var next = current + delta;

    if (next <= 0) {
      delete card.quantityByValue[value];
    } else if (delta > 0 && this.totalQuantity() >= this.quantityLimit) {
      this.showError(this.quantityLimitMessage());
      return;
    } else {
      card.quantityByValue[value] = next;
    }

    var activeVariant = card.quantityByValue[value] != null
      ? this.resolveVariantForValue(card, value)
      : null;
    if (activeVariant) {
      this.trackActiveVariant(activeVariant);
    } else {
      this.syncActiveVariant();
    }

    this.refreshSummary();
  };

  VariantSelector.prototype.cardItemCount = function (card) {
    var total = 0;
    Object.keys(card.quantityByValue).forEach(function (value) {
      total += card.quantityByValue[value];
    });
    return total;
  };

  VariantSelector.prototype.cardReadyItems = function (card) {
    if (!this.isGroupActive(card)) return [];

    var self = this;
    var items = [];

    Object.keys(card.quantityByValue).forEach(function (value) {
      var variant = self.resolveVariantForValue(card, value);
      if (variant && variant.available) {
        items.push({
          variant: variant,
          quantity: card.quantityByValue[value],
          member: (card.group && card.group.label) || '',
          sizeLabel: cleanSizeLabel(value, card.group && card.group.groupKey),
          optionValue: value,
        });
      }
    });

    return items;
  };

  VariantSelector.prototype.updateCardState = function (card) {
    var self = this;

    Object.keys(card.buttonsByPosition).forEach(function (positionKey) {
      var position = Number(positionKey);
      card.buttonsByPosition[position].forEach(function (button) {
        var value = button.getAttribute('data-value');
        var isActive = self.isMultiOption(position)
          ? card.quantityByValue[value] != null || !!(self.restockSelection &&
              self.restockSelection.card === card && self.restockSelection.value === value)
          : card.sharedSelections[position] === value;
        var activeClass = button.classList.contains('variant-selector-color-swatch')
          ? 'variant-selector-color-swatch--active'
          : 'variant-selector-swatch--active';
        button.classList.toggle(activeClass, isActive);
        setAttributeIfChanged(button, 'aria-pressed', isActive ? 'true' : 'false');
      });
    });

    this.scheduleQuantityRows(card);

    if (this.gradeSchoolLayout) {
      this.updateGradeSizeStepState(card);
    }

    var readyItems = this.cardReadyItems(card);

    if (card.priceEl) {
      setTextIfChanged(card.priceEl, this.groupDisplayPrice(card, readyItems));
    }

    if (card.subtitleEl) {
      var selectedSizeLabels = [];
      if (readyItems.length) {
        selectedSizeLabels = readyItems
          .map(function (item) {
            var multiValue = item.variant.options[self.multiOptionPosition - 1];
            var label = self.groupOption
              ? previewSizeLabel(multiValue, card.group.groupKey)
              : multiValue;
            return item.quantity > 1 ? label + ' ×' + item.quantity : label;
          })
          .filter(Boolean);
      } else {
        var selectedMultiValues = self.getSelectedMultiValues(card);
        if (selectedMultiValues.length) {
          selectedSizeLabels = selectedMultiValues
            .map(function (value) {
              var label = self.groupOption
                ? previewSizeLabel(value, card.group.groupKey)
                : value;
              var qty = card.quantityByValue[value];
              return qty > 1 ? label + ' ×' + qty : label;
            })
            .filter(Boolean);
        }
      }
      setSizeSummary(
        card.subtitleEl,
        selectedSizeLabels.length ? selectedSizeLabels : card.defaultSubtitle,
        selectedSizeLabels.length ? ', ' : ' - '
      );
    }

    if (card.badgeEl) {
      var selectedCount = self.cardItemCount(card);
      if (selectedCount > 0) {
        setHiddenIfChanged(card.badgeEl, false);
        setTextIfChanged(card.badgeEl, selectedCount + (selectedCount === 1 ? ' item' : ' items'));
      } else {
        setHiddenIfChanged(card.badgeEl, true);
        setTextIfChanged(card.badgeEl, '0 items');
      }
    }

    var isSelected = self.cardItemCount(card) > 0;
    card.el.classList.toggle('variant-selector-group--selected', isSelected);
  };

  VariantSelector.prototype.getDisplayPricePool = function (card, sizesOnly) {
    var variants = card.groupVariants;
    if (sizesOnly && this.groupOption) {
      var position = this.groupOption.position;
      var sizedVariants = variants.filter(function (variant) {
        return !isAccessoryLabel(cleanSizeLabel(variant.options[position - 1], card.group.groupKey));
      });
      if (sizedVariants.length) variants = sizedVariants;
    }
    var pool = variants.filter(function (variant) {
      return variant.available;
    });
    if (!pool.length) pool = variants.slice();
    if (!pool.length) return [];

    var selections = {};
    Object.keys(card.sharedSelections).forEach(function (positionKey) {
      selections[Number(positionKey)] = card.sharedSelections[positionKey];
    });

    if (!Object.keys(selections).length) return pool;

    return pool.filter(function (variant) {
      return Object.keys(selections).every(function (positionKey) {
        var position = Number(positionKey);
        return normalize(variant.options[position - 1]) === normalize(selections[position]);
      });
    });
  };

  VariantSelector.prototype.fromPriceCents = function (card) {
    var pool = this.getDisplayPricePool(card);
    if (!pool.length) return null;
    return pool.reduce(function (lowest, variant) {
      return variant.priceCents < lowest ? variant.priceCents : lowest;
    }, pool[0].priceCents);
  };

  VariantSelector.prototype.getDefaultPriceCents = function () {
    var available = this.variants.filter(function (variant) {
      return variant.available;
    });
    var pool = available.length ? available : this.variants;
    if (!pool.length) return null;
    return pool.reduce(function (lowest, variant) {
      return variant.priceCents < lowest ? variant.priceCents : lowest;
    }, pool[0].priceCents);
  };

  VariantSelector.prototype.trackActiveVariant = function (variant) {
    if (variant) this.activeVariantId = variant.id;
  };

  VariantSelector.prototype.initActiveVariant = function () {
    var initialId = this.getInitialVariantId();
    this.initialVariantId = initialId;
    if (!initialId) return;

    var variant = this.findVariantById(initialId);
    if (variant) this.activeVariantId = variant.id;
  };

  VariantSelector.prototype.isVariantInCurrentSelection = function (variant) {
    var self = this;
    return this.cards.some(function (card) {
      if (!self.isGroupActive(card)) return false;
      return Object.keys(card.quantityByValue).some(function (value) {
        var resolved = self.resolveVariantForValue(card, value);
        return resolved && resolved.id === variant.id;
      });
    });
  };

  VariantSelector.prototype.findVariantById = function (variantId) {
    var id = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].id === id) return this.variants[i];
    }
    return null;
  };

  VariantSelector.prototype.syncActiveVariant = function () {
    var active = this.getActiveVariant();
    this.activeVariantId = active ? active.id : null;
  };

  VariantSelector.prototype.hasResolvedSelection = function () {
    var self = this;
    return this.cards.some(function (card) {
      if (!self.isGroupActive(card)) return false;
      return (
        Object.keys(card.sharedSelections).length > 0 ||
        Object.keys(card.quantityByValue).length > 0
      );
    });
  };

  VariantSelector.prototype.getInitialVariantId = function () {
    var params = new URLSearchParams(window.location.search);
    var urlVariantId = parseInt(params.get('variant'), 10);
    if (urlVariantId && this.findVariantById(urlVariantId)) return urlVariantId;
    if (this.data.initialVariantId && this.findVariantById(this.data.initialVariantId)) {
      return this.data.initialVariantId;
    }
    return null;
  };

  VariantSelector.prototype.getActiveVariant = function () {
    if (this.restockSelection && this.isGroupActive(this.restockSelection.card)) {
      var restockVariant = this.resolveVariantForValue(
        this.restockSelection.card, this.restockSelection.value
      );
      if (restockVariant && !restockVariant.available) return restockVariant;
    }

    if (this.hasResolvedSelection()) {
      if (this.activeVariantId) {
        var tracked = this.findVariantById(this.activeVariantId);
        if (tracked && this.isVariantInCurrentSelection(tracked)) return tracked;
      }

      var ready = this.readyItems();
      if (ready.length) return ready[ready.length - 1].variant;

      for (var i = this.cards.length - 1; i >= 0; i--) {
        var card = this.cards[i];
        if (!this.isGroupActive(card)) continue;
        var preview = this.resolvePreviewVariant(card);
        if (preview) return preview;
      }

      return null;
    }

    var initialId = this.initialVariantId;
    if (initialId) return this.findVariantById(initialId);

    return null;
  };

  VariantSelector.prototype.getProductPagePriceCents = function () {
    var variant = this.getActiveVariant();
    if (variant) return variant.priceCents;
    return this.getDefaultPriceCents();
  };

  VariantSelector.prototype.getProductPageCompareAtCents = function (priceCents) {
    var variant = this.getActiveVariant();
    if (!variant || variant.compareAtCents <= variant.priceCents) return null;
    return variant.compareAtCents;
  };

  VariantSelector.prototype.setProductPagePriceText = function (el, srOnlyLabel, formattedPrice) {
    if (!el) return;
    var srOnly = el.querySelector('.sr-only');
    if (!srOnly && srOnlyLabel) {
      var sr = document.createElement('span');
      sr.className = 'sr-only';
      sr.textContent = srOnlyLabel;
      el.insertBefore(sr, el.firstChild);
    }
    var priceText = Array.prototype.find.call(el.childNodes, function (node) {
      return node.nodeType === 3 && node.nodeValue.trim();
    });
    if (priceText) {
      if (priceText.nodeValue !== formattedPrice) priceText.nodeValue = formattedPrice;
    } else {
      el.appendChild(document.createTextNode(formattedPrice));
    }
  };

  VariantSelector.prototype.updateProductPagePrice = function (activeVariant) {
    if (!this.productPagePriceEl) return;

    var variant = arguments.length ? activeVariant : this.getActiveVariant();
    var priceCents = variant ? variant.priceCents : this.getDefaultPriceCents();
    if (priceCents == null) return;

    this.setProductPagePriceText(
      this.productPagePriceEl,
      'Price: ',
      formatMoney(priceCents, this.moneyFormat)
    );

    var compareAtCents = variant && variant.compareAtCents > variant.priceCents ? variant.compareAtCents : null;
    if (this.productPageCompareEl) {
      if (compareAtCents != null && compareAtCents > priceCents) {
        setHiddenIfChanged(this.productPageCompareEl, false);
        this.setProductPagePriceText(
          this.productPageCompareEl,
          'Regular price: ',
          formatMoney(compareAtCents, this.moneyFormat)
        );
      } else {
        setTextIfChanged(this.productPageCompareEl, '');
        setHiddenIfChanged(this.productPageCompareEl, true);
      }
    }

    if (this.productPageInstallmentEl) {
      setTextIfChanged(this.productPageInstallmentEl, formatMoney(
        Math.floor(priceCents / 4),
        this.moneyFormat
      ));
    }
  };

  VariantSelector.prototype.fromPrice = function (card) {
    var cents = this.fromPriceCents(card);
    return cents == null ? '' : formatMoney(cents, this.moneyFormat);
  };

  VariantSelector.prototype.cartQuantity = function () {
    return this.readyItems().reduce(function (sum, item) {
      return sum + item.quantity;
    }, 0);
  };

  VariantSelector.prototype.groupDisplayPrice = function (card, readyItems) {
    if (readyItems.length) {
      var totalCents = readyItems.reduce(function (sum, item) {
        return sum + item.variant.priceCents * item.quantity;
      }, 0);
      return formatMoney(totalCents, this.moneyFormat);
    }

    var pool = this.getDisplayPricePool(card, true);
    if (!pool.length) return '';
    var priceCents = pool.reduce(function (lowest, variant) {
      return variant.priceCents < lowest ? variant.priceCents : lowest;
    }, pool[0].priceCents);
    return formatMoney(priceCents, this.moneyFormat);
  };

  VariantSelector.prototype.totalQuantity = function () {
    var total = this.defaultVariant ? this.defaultQuantity : 0;
    var self = this;
    this.cards.forEach(function (card) {
      if (self.isGroupActive(card)) {
        total += self.cardItemCount(card);
      }
    });
    return total;
  };

  VariantSelector.prototype.readyItems = function () {
    if (this.defaultVariant) {
      return this.defaultVariant.available
        ? [{ variant: this.defaultVariant, quantity: this.defaultQuantity }]
        : [];
    }

    var self = this;
    var all = [];
    this.cards.forEach(function (card) {
      all = all.concat(self.cardReadyItems(card));
    });
    return all;
  };

  VariantSelector.prototype.selectedGroupCount = function () {
    var self = this;
    return this.cards.filter(function (card) {
      return self.isGroupActive(card) && self.cardItemCount(card) > 0;
    }).length;
  };

  VariantSelector.prototype.selectedQuantity = function () {
    if (this.defaultVariant) return this.defaultQuantity;
    return this.totalQuantity();
  };

  VariantSelector.prototype.formatSelectionSummary = function (quantity) {
    if (arguments.length === 0) quantity = this.cartQuantity();
    if (!quantity) return this.emptySummaryText;
    return quantity === 1 ? this.copy('selectedOne', '1 item selected')
      : this.copy('selectedMany', '__count__ items selected', { count: quantity });
  };

  VariantSelector.prototype.isProductOutOfStock = function () {
    if (this.defaultVariant) {
      return !this.defaultVariant.available;
    }
    if (!this.variants.length) return true;
    return !this.variants.some(function (variant) {
      return variant.available;
    });
  };

  VariantSelector.prototype.getAvailabilityVariant = function (activeVariant) {
    if (this.defaultVariant) return this.defaultVariant;
    var variant = arguments.length ? activeVariant : this.getActiveVariant();
    if (!variant) return null;
    if (this.isVariantInCurrentSelection(variant)) return variant;
    if (this.restockSelection && this.isGroupActive(this.restockSelection.card)) {
      var selected = this.resolveVariantForValue(this.restockSelection.card, this.restockSelection.value);
      if (selected && selected.id === variant.id) return selected;
    }
    return null;
  };

  VariantSelector.prototype.hasUnavailableCompleteSelection = function (availabilityVariant) {
    var activeVariant = arguments.length ? availabilityVariant : this.getAvailabilityVariant();
    if (activeVariant && !activeVariant.available) return true;
    var self = this;
    return this.cards.some(function (card) {
      if (!self.isGroupActive(card)) return false;
      return Object.keys(card.quantityByValue).some(function (value) {
        var variant = self.resolveVariantForValue(card, value);
        return variant && !variant.available;
      });
    });
  };

  VariantSelector.prototype.updateStockNotice = function (availabilityVariant) {
    if (!this.availabilityEl) return;

    if (!this.stockNoticeEnabled) {
      setHiddenIfChanged(this.availabilityEl, true);
      return;
    }

    var variant = arguments.length ? availabilityVariant : this.getAvailabilityVariant();
    var available = variant ? !!variant.available : !this.isProductOutOfStock();
    var availableText = this.stockNotice.availableText || 'In Stock';
    var oosText = this.stockNotice.outOfStockText || 'Out of stock';

    setHiddenIfChanged(this.availabilityEl, false);
    setTextIfChanged(this.availabilityEl, available ? availableText : oosText);
    this.availabilityEl.classList.toggle('product-page__availability--available', available);
    this.availabilityEl.classList.toggle('product-page__availability--oos', !available);
  };

  VariantSelector.prototype.updateBackInStock = function (availabilityVariant, activeVariant) {
    var variant = arguments.length ? availabilityVariant : this.getAvailabilityVariant();
    if (!variant && this.isProductOutOfStock()) {
      variant = (arguments.length > 1 ? activeVariant : this.getActiveVariant()) || this.variants[0];
    }
    var variantId = variant ? String(variant.id) : '';
    var variantChanged = this.variantIdInput && this.variantIdInput.value !== variantId;
    if (variantChanged) {
      this.variantIdInput.value = variantId;
    }
    this.updateVariantUrl(variant && variant.id, variantChanged);

    var trigger = document.getElementById('klaviyo-notify-me');
    if (trigger) {
      var showTrigger = !!(variant && !variant.available);
      trigger.classList.toggle('variant-selector-restock-trigger--hidden', !showTrigger);
      var triggerDisplay = showTrigger ? 'flex' : 'none';
      if (trigger.style.display !== triggerDisplay) trigger.style.display = triggerDisplay;
    }
  };

  VariantSelector.prototype.scheduleSelectionRender = function () {
    var self = this;
    // Selection and cart state are already current in the model.
    // Render once after document-level click observers have read the event,
    // so their text reads do not force layout between our visual writes.
    if (this.selectionRenderPending) return;
    this.selectionRenderPending = true;
    var flush = function () { self.flushSelectionRender(); };
    this.selectionRenderTimer = window.setTimeout(flush, 100);
    if (document.visibilityState !== 'hidden') this.selectionRenderFrame = window.requestAnimationFrame(flush);
  };

  VariantSelector.prototype.flushSelectionRender = function () {
    if (!this.selectionRenderPending) return;
    this.selectionRenderPending = false;
    window.clearTimeout(this.selectionRenderTimer);
    if (this.selectionRenderFrame) window.cancelAnimationFrame(this.selectionRenderFrame);
    this.selectionRenderFrame = null;
    if (this.root.isConnected) this.refreshSummary();
  };

  VariantSelector.prototype.refreshSummary = function () {
    // A synchronous refresh supersedes a queued render (cart/reset/init).
    if (this.selectionRenderPending) {
      this.selectionRenderPending = false;
      window.clearTimeout(this.selectionRenderTimer);
      if (this.selectionRenderFrame) window.cancelAnimationFrame(this.selectionRenderFrame);
      this.selectionRenderFrame = null;
    }
    var self = this;
    var quantityLimitReached = this.totalQuantity() >= this.quantityLimit;
    this.cards.forEach(function (card) {
      var restockValue = self.restockSelection && self.restockSelection.card === card
        ? self.restockSelection.value : null;
      var stateKey = JSON.stringify([
        card.sharedSelections, card.quantityByValue, restockValue, self.isGroupActive(card)
      ]);
      if (card.renderedStateKey !== stateKey) {
        self.updateCardState(card);
        card.renderedStateKey = stateKey;
      }
      if (card.quantityRows) {
        card.quantityRows.forEach(function (controls) {
          if (controls.plus.disabled !== quantityLimitReached) controls.plus.disabled = quantityLimitReached;
        });
      }
    });

    var ready = this.readyItems();
    var quantity = ready.reduce(function (sum, item) { return sum + item.quantity; }, 0);
    // Reuse this render's resolved selection for price, stock and app input.
    // These values never persist across user actions or cart operations.
    var activeVariant = this.getActiveVariant();
    var availabilityVariant = this.getAvailabilityVariant(activeVariant);
    var isProductOos = this.isProductOutOfStock();
    var hasReadyItems = ready.length > 0;
    var hasUnavailableSelection = !hasReadyItems && this.hasUnavailableCompleteSelection(availabilityVariant);

    if (this.summaryEl) {
      setTextIfChanged(this.summaryEl, this.formatSelectionSummary(quantity));
    }

    if (this.priceEl) {
      if (!this.showSummaryPrice) {
        setHiddenIfChanged(this.priceEl, true);
      } else {
        var totalCents = ready.length
          ? ready.reduce(function (sum, item) {
              return sum + item.variant.priceCents * item.quantity;
            }, 0)
          : 0;
        if (this.priceValueEl) {
          setTextIfChanged(this.priceValueEl, formatMoney(totalCents, this.moneyFormat));
        } else {
          setTextIfChanged(this.priceEl, formatMoney(totalCents, this.moneyFormat));
        }
        setHiddenIfChanged(this.priceEl, !hasReadyItems);
      }
    }

    this.updateProductPagePrice(activeVariant);
    this.updateStockNotice(availabilityVariant);
    this.updateBackInStock(availabilityVariant, activeVariant);

    if (!this.addButton) return;

    if (this.footerEl) {
      this.footerEl.classList.toggle('variant-selector__footer--ready', hasReadyItems);
      this.footerEl.classList.toggle('variant-selector__footer--empty', !hasReadyItems);
    } else if (this.gradeSchoolLayout && this.addButton) {
      this.addButton.classList.toggle('variant-selector__atc-button--ready', hasReadyItems);
      this.addButton.classList.toggle('variant-selector__atc-button--empty', !hasReadyItems);
    }

    if (hasReadyItems) {
      if (this.addButton.disabled) this.addButton.disabled = false;
      setClassIfChanged(this.addButton, 'disabled', false);
      if (this.addLabel) {
        setHiddenIfChanged(this.addLabel, false);
        setTextIfChanged(this.addLabel, quantity === 1 ? this.copy('addOne', 'Add 1 item to bag')
          : this.copy('addMany', 'Add __count__ items to bag', { count: quantity }));
      }
      if (this.oosLabel) setClassIfChanged(this.oosLabel, 'hidden', true);
    } else if (isProductOos || hasUnavailableSelection) {
      if (!this.addButton.disabled) this.addButton.disabled = true;
      setClassIfChanged(this.addButton, 'disabled', true);
      if (this.addLabel) setHiddenIfChanged(this.addLabel, true);
      if (this.oosLabel) setClassIfChanged(this.oosLabel, 'hidden', false);
    } else {
      if (!this.addButton.disabled) this.addButton.disabled = true;
      setClassIfChanged(this.addButton, 'disabled', true);
      if (this.addLabel) {
        setHiddenIfChanged(this.addLabel, false);
        setTextIfChanged(this.addLabel, this.emptyButtonLabel());
      }
      if (this.oosLabel) setClassIfChanged(this.oosLabel, 'hidden', true);
    }
  };

  VariantSelector.prototype.setLoading = function (isLoading) {
    this.isLoading = !!isLoading;
    if (this.root) {
      this.root.classList.toggle('variant-selector--loading', this.isLoading);
    }
    if (this.addButton) {
      if (this.isLoading) {
        this.addButton.disabled = true;
      }
      this.addButton.setAttribute('aria-busy', this.isLoading ? 'true' : 'false');
    }
  };

  VariantSelector.prototype.bindAddToCart = function () {
    var self = this;
    if (!this.addButton) return;
    this.addButton.removeAttribute('onclick');
    this.addButton.addEventListener('click', function (event) {
      event.preventDefault();
      self.addToCart();
    });
  };

  VariantSelector.prototype.collectEmbroidery = function () {
    var checkbox = document.querySelector('#Embroidery #embroidery-checkbox');
    var input = document.querySelector('#Embroidery #embroidery-text');
    var value = input ? input.value.trim() : '';
    if (!checkbox || !checkbox.checked || !value) return null;
    return {
      id: EMBROIDERY_VARIANT_ID,
      quantity: 1,
      properties: { For: this.productTitle, Embroidery: value, is_final_sale: 'true' },
    };
  };

  VariantSelector.prototype.showError = function (message) {
    if (!this.errorEl) return;

    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    }

    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    this.errorEl.focus();

    var self = this;
    this.errorTimeout = setTimeout(function () {
      self.errorEl.hidden = true;
      self.errorEl.textContent = '';
      self.errorTimeout = null;
    }, 3000);
  };

  VariantSelector.prototype.resetAfterAdd = function () {
    var self = this;
    this.restockSelection = null;
    this.cards.forEach(function (card) {
      card.quantityByValue = {};
      card.sharedSelections = {};
      if (!self.isSingleGroup) {
        self.setCardExpanded(card, false);
      }
      self.updateAvailability(card);
    });
    this.refreshSummary();
  };

  VariantSelector.prototype.addToCart = function () {
    var self = this;
    if (this.isLoading) return;

    this.flushSelectionRender();
    this.flushQuantityRows();
    if (this.flushVariantSync) this.flushVariantSync();
    var ready = this.readyItems();
    if (!ready.length) {
      this.showError(this.emptyButtonLabel() + '.');
      return;
    }

    if (this.totalQuantity() > this.quantityLimit) {
      this.showError(this.quantityLimitMessage());
      return;
    }

    // Matching-set PDPs (Mom & Me, family groups) share one id per product so a
    // later Child add can join an earlier Mom add. Other multi-line adds still get
    // a unique id for that one decision. One-off singles stay untagged.
    var setId = null;
    if (this.isMatchingSetProduct() && this.productId) {
      setId = 'set-product-' + this.productId;
    } else if (ready.length > 1) {
      setId = newSetId();
    }

    var items = ready.map(function (item) {
      var payload = { id: item.variant.id, quantity: item.quantity };
      if (setId) {
        // Only send properties that carry a value: the cart drawer treats a missing
        // property as "not applicable", and an empty string is truthy in Liquid.
        payload.properties = { _set_id: setId };
        if (item.member) payload.properties._set_member = item.member;
        if (item.sizeLabel) payload.properties._set_size = item.sizeLabel;
        if (item.optionValue) payload.properties._set_option = item.optionValue;
      }
      return payload;
    });

    var embroidery = this.collectEmbroidery();
    var items = [];
    ready.forEach(function (item) {
      var line = { id: item.variant.id, quantity: item.quantity };
      if (embroidery) {
        var pairing = window.crypto.randomUUID();
        line.properties = {
          Embroidery: embroidery.properties.Embroidery,
          is_final_sale: 'true',
          'Return policy': checkbox.dataset.finalSaleLabel,
          _embroidery_pair: pairing,
        };
        items.push(line);
        items.push({
          id: EMBROIDERY_VARIANT_ID,
          quantity: item.quantity,
          parent_id: item.variant.id,
          properties: {
            For: embroidery.properties.For,
            Size: item.variant.options.join(' / '),
            Embroidery: embroidery.properties.Embroidery,
            is_final_sale: 'true',
            _embroidery_pair: pairing,
          },
        });
      } else {
        items.push(line);
      }
    });

    // Commit the current variant URL before cart/app events read the page URL.
    if (this.flushVariantUrl) this.flushVariantUrl();
    this.setLoading(true);

    var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(cartRoot + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then(function () {
        self.resetAfterAdd();
        document.dispatchEvent(
          new CustomEvent('cart:item-added', { detail: { openDrawer: true } })
        );
      })
      .catch(function () {
        self.showError('Something went wrong. Please try again.');
      })
      .finally(function () {
        self.setLoading(false);
        self.refreshSummary();
      });
  };

  VariantSelector.prototype.updateVariantUrl = function (variantId, notifyChange) {
    var self = this;
    var nextVariantId = variantId ? String(variantId) : '';
    var pathname = window.location.pathname;
    // Quantity/summary refreshes must not restart URL work for the same variant.
    // A real input change still follows the existing notification path below.
    var needsUrlSync = this.pendingVariantId !== nextVariantId || this.variantUrlPath !== pathname;
    this.pendingVariantId = nextVariantId;
    this.variantUrlPath = pathname;
    if (needsUrlSync) {
    if (this.variantUrlTimer) window.clearTimeout(this.variantUrlTimer);
    if (this.variantUrlIdle && window.cancelIdleCallback) window.cancelIdleCallback(this.variantUrlIdle);
    this.flushVariantUrl = function () {
      window.clearTimeout(self.variantUrlTimer);
      if (self.variantUrlIdle && window.cancelIdleCallback) window.cancelIdleCallback(self.variantUrlIdle);
      self.variantUrlTimer = null;
      self.variantUrlIdle = null;
      if (!self.root.isConnected || window.location.pathname !== pathname) return;
      var url = new URL(window.location.href);
      if (self.pendingVariantId) url.searchParams.set('variant', self.pendingVariantId);
      else url.searchParams.delete('variant');
      var nextUrl = url.pathname + url.search + url.hash;
      if (nextUrl !== window.location.pathname + window.location.search + window.location.hash) {
        window.history.replaceState(window.history.state, document.title, nextUrl);
      }
    };
    // Clarity restarts a page recording on query-string history changes. Wait
    // for a short selection pause and an idle slot, so a family/size sequence
    // commits its final URL once. Keep every app input-change notification below.
    this.variantUrlTimer = window.setTimeout(function () {
      if (window.requestIdleCallback && document.visibilityState !== 'hidden') {
        self.variantUrlIdle = window.requestIdleCallback(self.flushVariantUrl, { timeout: 1000 });
      } else self.flushVariantUrl();
    }, document.visibilityState === 'hidden' ? 0 : 800);
    }
    this.variantChangePending = this.variantChangePending || !!notifyChange;
    if (this.variantSyncScheduled) return;
    if (!this.variantChangePending) return;

    this.variantSyncScheduled = true;
    var generation = (this.variantSyncGeneration || 0) + 1;
    this.variantSyncGeneration = generation;
    var idleDeadlineAt = window.performance.now() + 250;
    function syncVariant(deadline) {
      if (generation !== self.variantSyncGeneration) return;
      self.variantSyncIdle = null;
      // Let already queued shopping input run before nonessential app refreshes.
      var scheduling = window.navigator && window.navigator.scheduling;
      var inputPending = false;
      try {
        inputPending = !!(scheduling && typeof scheduling.isInputPending === 'function' && scheduling.isInputPending());
      } catch (error) { /* An unavailable scheduling hint must not suppress app events. */ }
      if (deadline && !deadline.didTimeout && window.performance.now() < idleDeadlineAt && inputPending) {
        self.variantSyncIdle = window.requestIdleCallback(syncVariant, { timeout: Math.max(0, idleDeadlineAt - window.performance.now()) });
        return;
      }
      self.flushVariantSync();
    }
    this.flushVariantSync = function () {
      if (!self.variantSyncScheduled) return;
      self.variantSyncGeneration++;
      if (self.variantSyncFrame) window.cancelAnimationFrame(self.variantSyncFrame);
      if (self.variantSyncTimer != null) window.clearTimeout(self.variantSyncTimer);
      if (self.variantSyncIdle != null && window.cancelIdleCallback) window.cancelIdleCallback(self.variantSyncIdle);
      self.variantSyncFrame = null;
      self.variantSyncTimer = null;
      self.variantSyncIdle = null;
      self.variantSyncScheduled = false;
      var shouldNotify = self.variantChangePending;
      self.variantChangePending = false;
      if (!self.root.isConnected || window.location.pathname !== pathname) return;
      if (shouldNotify && self.variantIdInput) {
        self.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    function afterPaint() {
      if (generation !== self.variantSyncGeneration) return;
      self.variantSyncTimer = null;
      if (window.requestIdleCallback && document.visibilityState !== 'hidden') {
        self.variantSyncIdle = window.requestIdleCallback(syncVariant, { timeout: 250 });
      } else syncVariant();
    }
    // Preserve native change notifications and current values, while allowing
    // first-use app analytics to run after the visual update in an idle slot.
    if (document.visibilityState === 'hidden') {
      self.variantSyncTimer = window.setTimeout(syncVariant, 0);
    } else {
      self.variantSyncFrame = window.requestAnimationFrame(function () {
        self.variantSyncFrame = null;
        self.variantSyncTimer = window.setTimeout(afterPaint, 0);
      });
    }
  };

  VariantSelector.prototype.switchGalleryImage = function (imageUrl) {
    if (!imageUrl) return;
    this.pendingGalleryImage = imageUrl;
    if (this.galleryImagePending) return;
    this.galleryImagePending = true;
    var self = this;
    var flush = function () {
      if (!self.galleryImagePending) return;
      self.galleryImagePending = false;
      window.clearTimeout(self.galleryImageTimer);
      if (self.galleryImageFrame) window.cancelAnimationFrame(self.galleryImageFrame);
      self.galleryImageFrame = null;
      if (self.root.isConnected) self.renderGalleryImage(self.pendingGalleryImage);
    };
    this.galleryImageTimer = window.setTimeout(flush, 100);
    if (document.visibilityState !== 'hidden') this.galleryImageFrame = window.requestAnimationFrame(flush);
  };

  VariantSelector.prototype.galleryPhotoLookup = function (root, imageUrl) {
    var cache = this.galleryPhotoCache;
    if (!cache || cache.root !== root) {
      if (cache && cache.observer) cache.observer.disconnect();
      cache = { root: root, entries: new Map(), observer: null };
      if (typeof MutationObserver === 'function') {
        cache.observer = new MutationObserver(function () { cache.entries.clear(); });
        cache.observer.observe(root, { childList: true, subtree: true, attributes: true,
          attributeFilter: ['src', 'data-gallery-src', 'data-gallery-index'] });
      }
      this.galleryPhotoCache = cache;
    }
    if (!cache.observer) cache.entries.clear();
    else if (cache.observer.takeRecords().length) cache.entries.clear();
    var desktop = window.innerWidth >= 990;
    var key = (desktop ? 'desktop:' : 'mobile:') + imageUrl;
    if (cache.entries.has(key)) return cache.entries.get(key);
    var result = { grid: desktop ? root.querySelector('#media-gallery-grid') : null, items: [], images: [], targetIndex: -1 };
    if (result.grid) {
      result.grid.querySelectorAll('.media-gallery-grid-item').forEach(function (item) {
        var image = item.querySelector('img');
        if (image && image.src.indexOf(imageUrl) !== -1) result.items.push(item);
      });
    }
    if (!desktop) root.querySelectorAll('img.media-gallery__main').forEach(function (image, index) {
      var source = image.getAttribute('data-gallery-src') || image.src;
      if (source.indexOf(imageUrl) === -1) return;
      var slide = image.closest('.swiper-slide');
      result.targetIndex = slide ? Number(slide.dataset.galleryIndex) : index;
      result.images.push(image);
    });
    cache.entries.set(key, result);
    return result;
  };

  VariantSelector.prototype.renderGalleryImage = function (imageUrl) {
    if (!imageUrl) return;
    var root = document.getElementById('media-gallery-root');
    if (!root) return;
    var photo = this.galleryPhotoLookup(root, imageUrl);
    if (window.innerWidth >= 990) {
      if (!photo.grid) return;
      photo.items.forEach(function (item) {
        if (item !== photo.grid.firstElementChild) photo.grid.insertBefore(item, photo.grid.firstChild);
      });
      return;
    }
    if (photo.targetIndex < 0) return;
    var mainSwiper = root.querySelector('.media-gallery-main');
    var swiper = mainSwiper && mainSwiper.swiper;
    var active = swiper && swiper.slides[swiper.activeIndex];
    var snap = swiper && swiper.snapGrid[swiper.snapIndex];
    var aligned = active && Number(active.dataset.galleryIndex) === photo.targetIndex && !swiper.animating &&
      typeof snap === 'number' && Math.abs(Math.abs(swiper.translate) - snap) < 1;
    photo.images.forEach(function (image) {
      var pending = image.getAttribute('data-gallery-src');
      if (!pending) return;
      var pendingSet = image.getAttribute('data-gallery-srcset');
      if (pendingSet) image.setAttribute('srcset', pendingSet);
      image.setAttribute('src', pending);
      image.removeAttribute('data-gallery-srcset');
      image.removeAttribute('data-gallery-src');
    });
    if (!swiper || aligned) return;
    if (swiper.params.loop && typeof swiper.slideToLoop === 'function') swiper.slideToLoop(photo.targetIndex, 300);
    else swiper.slideTo(photo.targetIndex, 300);
  };

  function init() {
    document.querySelectorAll('[data-variant-selector]').forEach(function (root) {
      new VariantSelector(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();






